/**
 * Class selection and subject picking page.
 * Fetches classes for a school year, loads details, and selects subjects.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Container,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  SelectChangeEvent,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  IconButton
} from '@mui/material';
import { classesService } from '../services/api.service';
import { ClassInfo } from '../types/class.types';

interface LocationState {
  schoolYear: string;
  quarter: string;
}

interface SubjectEntry {
  subject: string;
  subject_id: string;
  teacher: string;
  neolms_id: string;
  selected: boolean;
}

/**
 * Step 2 of the workflow: choose class and subjects to process.
 */
const ClassSelection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { schoolYear, quarter } = location.state as LocationState || {};
  
  const [classes, setClasses] = useState<ClassInfo[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [classData, setClassData] = useState<ClassInfo | null>(null);
  const [subjects, setSubjects] = useState<SubjectEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Redirect back if no school year or quarter was selected
    if (!schoolYear || !quarter) {
      navigate('/select-school-year');
      return;
    }
    
    fetchClasses();
  }, [schoolYear, navigate, quarter]);

  const fetchClasses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const classesData = await classesService.getClasses(schoolYear);
      setClasses(classesData.classes || []);
    } catch (err) {
      setError('Failed to fetch classes. Please try again.');
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = async (event: SelectChangeEvent) => {
    const className = event.target.value;
    setSelectedClass(className);
    
    if (className) {
      setDetailsLoading(true);
      setClassData(null);
      setSubjects([]);
      
      try {
        const response = await classesService.getClassDetail(className);
        const classData = response.class_data;
        setClassData(classData);
        
        // Extract and process subjects from class data
        parseSubjects(classData, className);
      } catch (err) {
        setError('Failed to fetch class details. Please try again.');
        console.error('Error fetching class details:', err);
      } finally {
        setDetailsLoading(false);
      }
    }
  };

  const parseSubjects = (classData: ClassInfo, className: string) => {
    try {
      // Parse class name to get grade level
      const [year, section] = className.split('@').map(part => part.trim());
      const parts = section.split('-').map(part => part.trim());
      const gradeLevel = parts[0];
      
      // Choose the correct subject list based on grade level
      let subjectsList: any[] = [];
      // Original quarter-based logic (kept for reference):
      // if (gradeLevel === 'Grade 11' || gradeLevel === 'Grade 12') {
      //   // Senior High
      //   // Check quarter
      //   if (quarter === 'First Quarter' || quarter === 'Second Quarter') {
      //      subjectsList = classData.class_schedule_shs || [];
      // } else {
      //   // Quarter is Third Quarter or Fourth Quarter
      //   subjectsList = classData.class_schedule_2 || [];
      // }
      // } else {
      //   // Regular grades
      //   subjectsList = classData.class_schedule || [];
      // }
      if (gradeLevel === 'Grade 11' || gradeLevel === 'Grade 12') {
        // Senior High - choose schedule based on term
        if (quarter === 'Term 1' || quarter === 'Term 2') {
          subjectsList = classData.class_schedule_shs || [];
        } else {
          // Term 3
          subjectsList = classData.class_schedule_2 || [];
        }
      } else {
        // Regular grades
        subjectsList = classData.class_schedule || [];
      }
      
      // Format subjects
      const formattedSubjects: SubjectEntry[] = subjectsList.map(subject => {
        if (gradeLevel === 'Grade 11' || gradeLevel === 'Grade 12') {
          // Senior High format
          return {
            subject: subject.specialized_subject || 'N/A',
            subject_id: subject.specialized_subject || 'N/A',
            teacher: subject.teacher || 'N/A',
            neolms_id: subject.neolms_id || '',
            selected: true
          };
        } else {
          // Regular format
          return {
            subject: subject.subject || 'N/A',
            subject_id: subject.subject || 'N/A',
            teacher: subject.teacher || 'N/A',
            neolms_id: subject.neolms_id || '',
            selected: true
          };
        }
      });
      
      setSubjects(formattedSubjects);
    } catch (error) {
      console.error('Error parsing subjects:', error);
      setSubjects([]);
    }
  };

  const handleNeolmsIdChange = (index: number, value: string) => {
    const updatedSubjects = [...subjects];
    updatedSubjects[index].neolms_id = value;
    setSubjects(updatedSubjects);
  };

  const handleSubjectSelection = (index: number, checked: boolean) => {
    const updatedSubjects = [...subjects];
    updatedSubjects[index].selected = checked;
    setSubjects(updatedSubjects);
  };

  const toggleAllSubjects = (selected: boolean) => {
    const updatedSubjects = subjects.map(subject => ({
      ...subject,
      selected
    }));
    setSubjects(updatedSubjects);
  };

  const handleContinue = () => {
    // Navigate to grade processing page with selected class and subject information
    navigate('/grade-processing', {
      state: {
        schoolYear,
        quarter,
        className: selectedClass,
        classData,
        subjects: subjects.filter(subject => subject.selected)
      }
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Select Class
        </Typography>
        
        <Typography variant="body1" paragraph align="center" sx={{ mb: 4 }}>
          {schoolYear} - {quarter}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="class-label">Class</InputLabel>
                    <Select
                      labelId="class-label"
                      id="class-select"
                      value={selectedClass}
                      label="Class"
                      onChange={handleClassChange}
                    >
                      {classes.map((cls) => (
                        <MenuItem key={cls.name} value={cls.name}>
                          {cls.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {detailsLoading && (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {classData && (
                <Card variant="outlined" sx={{ mt: 3, mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Class Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Class Name:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {classData.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          School Year:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {classData.school_year}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Section:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {classData.section}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Class Adviser:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {classData.teacher || 'Not Available'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {subjects.length > 0 && (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Class Schedule
                        </Typography>
                        
                        <Box display="flex" justifyContent="flex-start" mb={2}>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => toggleAllSubjects(true)}
                            sx={{ mr: 1 }}
                          >
                            Select All
                          </Button>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => toggleAllSubjects(false)}
                          >
                            Deselect All
                          </Button>
                        </Box>
                        
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">Process</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Teacher</TableCell>
                                <TableCell>NeoLMS ID</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subjects.map((subject, index) => (
                                <TableRow key={index}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={subject.selected}
                                      onChange={(e) => handleSubjectSelection(index, e.target.checked)}
                                    />
                                  </TableCell>
                                  <TableCell>{subject.subject}</TableCell>
                                  <TableCell>{subject.teacher}</TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      variant="outlined"
                                      value={subject.neolms_id}
                                      onChange={(e) => handleNeolmsIdChange(index, e.target.value)}
                                      inputProps={{ 
                                        style: { padding: '8px', width: '80px' }
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                    
                    {classData.students && classData.students.length > 0 && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                          Students
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Last Name</TableCell>
                                <TableCell>First Name</TableCell>
                                <TableCell>Gender</TableCell>
                                <TableCell>Grade Level</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {classData.students.map((student, index) => (
                                <TableRow key={index}>
                                  <TableCell>{student.last_name}</TableCell>
                                  <TableCell>{student.first_name}</TableCell>
                                  <TableCell>{student.gender}</TableCell>
                                  <TableCell>{student.level}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Box mt={3} display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/select-school-year', { state: { schoolYear, quarter } })}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  disabled={!selectedClass || (subjects.length > 0 && !subjects.some(subject => subject.selected))}
                >
                  Get Grades
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ClassSelection;